import {
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { UserName, UserNameDTO } from "../types/userName";
import { KeycloakEmail, KeycloakEmailDTO } from "../types/keycloakEmail";
import { BoUserRole } from "./boUserRole";
/* eslint-disable import/export */
export abstract class AddBOUserRPC implements
  LeoRPC<AddBOUserRPC.Request, AddBOUserRPC.Response, AddBOUserRPC.Errors.Errors>
{
  abstract execute(
    request: AddBOUserRPC.Request
  ): Promise<LeoRPCResult<AddBOUserRPC.Response, AddBOUserRPC.Errors.Errors>>;
}

export namespace AddBOUserRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    userName: UserNameDTO;
    boEmailAddress: KeycloakEmailDTO;
    boUserRole: BoUserRole.BoUserRoleDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isAddBOUserRPCRequest: boolean = true;
  
    readonly userName: UserName;
    readonly boEmailAddress: KeycloakEmail;
    readonly boUserRole: BoUserRole.BoUserRole;
  
  
    constructor(
    userName: UserName, 
    boEmailAddress: KeycloakEmail, 
    boUserRole: BoUserRole.BoUserRole
    ) {
      this.userName = userName;
      this.boEmailAddress = boEmailAddress;
      this.boUserRole = boUserRole;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const userName = UserName.fromDTO(getObject(dto, "userName", InvalidRequestUserNameError));
      const boEmailAddress = KeycloakEmail.fromDTO(getObject(dto, "boEmailAddress", InvalidRequestBoEmailAddressError));
      const boUserRole = BoUserRole.fromDTO(getObject(dto, "boUserRole", InvalidRequestBoUserRoleError));
      return new Request(
        userName,
        boEmailAddress,
        boUserRole
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          userName: this.userName.toDTO(),
          boEmailAddress: this.boEmailAddress.toDTO(),
          boUserRole: BoUserRole.toDTO(this.boUserRole)
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `AddBOUserRPC(userName='${this.userName}',boEmailAddress='${this.boEmailAddress}',boUserRole='${this.boUserRole}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `AddBOUserRPC.Response()`;
  };

  export enum RPCError {
    SpocCannotBeBoUser = "SPOC_CANNOT_BE_BO_USER",
    BoUserAlreadyExists = "BO_USER_ALREADY_EXISTS",
    UnableToAddBoUser = "UNABLE_TO_ADD_BO_USER"
  }

  export namespace Errors {
    
    export interface SpocCannotBeBoUserDTO {
      code: string;
    }
    
    export class SpocCannotBeBoUser extends LeoRPCError<RPCError> {
      readonly isSpocCannotBeBoUser: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SpocCannotBeBoUser;
        super(code);
      }
    
    
      toDTO(): SpocCannotBeBoUserDTO {
        const dto: SpocCannotBeBoUserDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SpocCannotBeBoUser.prototype.toString = function toString(): string {
      return `AddBOUserRPC.SPOC_CANNOT_BE_BO_USER()`;
    };
    
    
    export interface BoUserAlreadyExistsDTO {
      code: string;
    }
    
    export class BoUserAlreadyExists extends LeoRPCError<RPCError> {
      readonly isBoUserAlreadyExists: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.BoUserAlreadyExists;
        super(code);
      }
    
    
      toDTO(): BoUserAlreadyExistsDTO {
        const dto: BoUserAlreadyExistsDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    BoUserAlreadyExists.prototype.toString = function toString(): string {
      return `AddBOUserRPC.BO_USER_ALREADY_EXISTS()`;
    };
    
    
    export interface UnableToAddBoUserDTO {
      code: string;
    }
    
    export class UnableToAddBoUser extends LeoRPCError<RPCError> {
      readonly isUnableToAddBoUser: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnableToAddBoUser;
        super(code);
      }
    
    
      toDTO(): UnableToAddBoUserDTO {
        const dto: UnableToAddBoUserDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnableToAddBoUser.prototype.toString = function toString(): string {
      return `AddBOUserRPC.UNABLE_TO_ADD_BO_USER()`;
    };
    

    export type Errors = SpocCannotBeBoUser | BoUserAlreadyExists | UnableToAddBoUser;
    export type ErrorsDTO = SpocCannotBeBoUserDTO | BoUserAlreadyExistsDTO | UnableToAddBoUserDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.SpocCannotBeBoUser:
          return new SpocCannotBeBoUser();
        case RPCError.BoUserAlreadyExists:
          return new BoUserAlreadyExists();
        case RPCError.UnableToAddBoUser:
          return new UnableToAddBoUser();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestUserNameError extends AddBOUserRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestUserNameError";
  }
}

export class InvalidRequestBoEmailAddressError extends AddBOUserRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoEmailAddressError";
  }
}

export class InvalidRequestBoUserRoleError extends AddBOUserRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestBoUserRoleError";
  }
}


export class InvalidResponseSpocCannotBeBoUserError extends AddBOUserRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSpocCannotBeBoUserError";
  }
}

export class InvalidResponseBoUserAlreadyExistsError extends AddBOUserRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseBoUserAlreadyExistsError";
  }
}

export class InvalidResponseUnableToAddBoUserError extends AddBOUserRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnableToAddBoUserError";
  }
}

